/**
 * Stela 2 — módulo de tokens de diseño (fuente única).
 *
 * Fuente: Claude Design.
 *   - Stela 2 Design System: 9bedf2f6-d8d5-479e-9ca5-415ab8591849
 *   - Proyecto Stela 2 (tokens.css): 019e1a81-9933-7157-9dd7-62dfac81997e
 * Fecha de sincronización: 2026-09-24.
 *
 * Regla de colisión (decisión del usuario, 24-sep-2026): cuando dos fuentes
 * chocan, manda siempre Claude Design.
 *   - Si un token existe en Claude Design, se usa su valor aunque Angular
 *     tenga otro.
 *   - Entre las dos fuentes de Claude Design, el Stela 2 Design System
 *     manda en los colores de estado y los radios. El esquema oscuro es el
 *     berenjena de theme-kit, no el navy del design system (29-sep-2026).
 *   - El tokens.css del proyecto Stela 2 manda en los colores de marca que
 *     el design system no define: aubergine, terracotta, azure, mobile y
 *     los gold y navy light/dark.
 *   - Solo se conserva el valor de Angular en los tokens que no existen en
 *     ninguna de las dos fuentes.
 *
 * Las variables `--stela-*` se generan desde este módulo con `npm run tokens:css`
 * (`stela2.tokens.css`) y el tema de Tailwind con `npm run tokens:tailwind`
 * (`stela2.tokens.theme.css`); `stela2.tokens.spec.ts` falla si alguno se desfasa.
 */

export const stela2Tokens = {
  brand: {
    gold: '#C9A84C',
    goldLight: '#D4B965',
    goldDark: '#B0923E',
    goldSoft: '#F4EAC6',

    navy: '#1A2744',
    navyLight: '#243356',
    navyDark: '#111B30',
    navySoft: '#E8ECF5',

    aubergine: '#3E2C42',
    aubergineLight: '#4D3851',
    aubergineDark: '#2A1E2E',
    aubergineDeep: '#1F1622',

    terracotta: '#D4956A',
    terracottaHover: '#C4854A',

    azure: '#2F2F9E',
    azureHover: '#1F1F7E',

    mobileBg: '#000C33',
    mobileSurface: '#272E55',
    mobileSurface2: '#585D7B',

    // Fuente: tokens.css del proyecto Stela 2 (`blush: "#FDEEEE"` en el
    // `tailwind.config.js` original). No existe en el design system, así
    // que manda el proyecto Stela 2 (regla de colisión).
    blush: '#FDEEEE',
  },

  neutral: {
    canvas: '#F5F6F8',
    canvas2: '#EEF0F4',
    surface: '#FFFFFF',
    surfaceSubtle: '#FAFBFC',
    line: '#E8EAED',
    lineSoft: '#EEEEEE',
    muted: '#EEF0F3',
    fg: '#2D3436',
    fg1: '#1A2744',
    fg2: '#4A5568',
    fg3: '#718096',
    fg4: '#A0AEC0',
  },

  status: {
    success: { DEFAULT: '#16894C', soft: '#E7F4EC', dark: '#0E6B3A' },
    warning: { DEFAULT: '#9F7400', soft: '#FFF5D6' },
    danger: { DEFAULT: '#BA151B', soft: '#FBECEC', dark: '#A0181C' },
    info: { DEFAULT: '#3B82F6', soft: '#EFF6FF' },
  },

  radius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    pill: '999px',
  },

  font: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    display: "'Playfair Display', 'Cormorant Garamond', Georgia, 'Times New Roman', serif",
    mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  },

  /**
   * Esquema oscuro berenjena, el mismo de `stela2.tokens.css` (bloque `html[data-color-scheme="dark"]`, que sigue
   * `docs/Stela 2/styles-dark-responsive.css`). Sustituye al bloque `.dark` de `src/index.css` del
   * Stela 2 Design System, que era navy (hsl 221): decisión del usuario del 29-sep-2026, porque ese
   * azul no casa con el diseño de referencia. Alimenta la rampa `colorScheme.dark.surface` de Optimus,
   * así que de aquí salen `--p-content-background`, `--p-content-hover-background`, los overlays, etc.
   */
  darkScheme: {
    background: '#1A1019', // --stela-bg
    foreground: '#ECE4E8', // --stela-fg
    card: '#2A1E2E', // --stela-surface
    cardForeground: '#ECE4E8', // --stela-fg
    popover: '#2A1E2E', // --stela-surface
    popoverForeground: '#ECE4E8', // --stela-fg
    primary: '#C9A84C', // gold
    primaryForeground: '#2A1E2E', // aubergine-dark
    secondary: '#3E2C42', // aubergine
    secondaryForeground: '#ECE4E8', // --stela-fg
    muted: '#322631', // --stela-line-soft
    mutedForeground: '#9C8C96', // --stela-fg-3
    accent: '#4D3851', // aubergine-light
    accentForeground: '#F8F2F4', // --stela-fg-1
    destructive: '#CE171E', // hsl(357.8 79.7% 45%)
    destructiveForeground: '#FFFFFF', // hsl(0 0% 100%)
    border: '#3D2E40', // --stela-line
    input: '#3D2E40', // --stela-line
    ring: '#C9A84C', // gold
  },
} as const;

export type Stela2Tokens = typeof stela2Tokens;

/**
 * Vocabulario de marca para `theme.extend.colors` de Tailwind, derivado de
 * `stela2Tokens`. Se mantienen `primary` y `surface` de Optimus (no se
 * declaran aquí) y no se importan los nombres semánticos de shadcn
 * (`muted`, `accent`, `card`, `destructive`, `border`, `foreground`,
 * `background`, `input`, `ring`, `popover`).
 */
export function toTailwindColors() {
  const { brand, neutral, status } = stela2Tokens;

  return {
    gold: {
      DEFAULT: brand.gold,
      light: brand.goldLight,
      dark: brand.goldDark,
      soft: brand.goldSoft,
    },
    navy: {
      DEFAULT: brand.navy,
      light: brand.navyLight,
      dark: brand.navyDark,
      soft: brand.navySoft,
    },
    canvas: neutral.canvas,
    line: {
      DEFAULT: neutral.line,
      soft: neutral.lineSoft,
    },
    fg: {
      DEFAULT: neutral.fg,
      1: neutral.fg1,
      2: neutral.fg2,
      3: neutral.fg3,
      4: neutral.fg4,
    },
    success: {
      DEFAULT: status.success.DEFAULT,
      soft: status.success.soft,
      dark: status.success.dark,
    },
    warning: {
      DEFAULT: status.warning.DEFAULT,
      soft: status.warning.soft,
    },
    danger: {
      DEFAULT: status.danger.DEFAULT,
      soft: status.danger.soft,
      dark: status.danger.dark,
    },
    blush: brand.blush,
  };
}

/** Fuentes de marca para `theme.extend.fontFamily`, como arrays de Tailwind. */
export function toTailwindFonts() {
  const { font } = stela2Tokens;
  const toArray = (stack: string) => stack.split(',').map((part) => part.trim());

  return {
    sans: toArray(font.sans),
    display: toArray(font.display),
    mono: toArray(font.mono),
  };
}

/**
 * Bloque `@theme` de Tailwind 4 con el vocabulario de marca: cada color de
 * `toTailwindColors()` pasa a `--color-<nombre>[-<variante>]` (`DEFAULT` no
 * añade sufijo) y cada fuente de `toTailwindFonts()` a `--font-<nombre>`.
 * Es `inline` para que las utilidades lleven el valor literal, como en
 * Tailwind 3: `theme.css` (Inspinia) redefine `--color-success`,
 * `--color-warning` y `--color-danger` en `:root` y, con `var()`, `border-success`
 * y compañía saldrían con sus colores.
 * `npm run tokens:tailwind` lo escribe en `stela2.tokens.theme.css`.
 */
export function toTailwindThemeCss(): string {
  const lines: string[] = [];

  for (const [name, value] of Object.entries(toTailwindColors())) {
    if (typeof value === 'string') {
      lines.push(`  --color-${name}: ${value};`);
      continue;
    }
    for (const [variant, color] of Object.entries(value)) {
      const suffix = variant === 'DEFAULT' ? '' : `-${variant}`;
      lines.push(`  --color-${name}${suffix}: ${color};`);
    }
  }

  for (const [name, stack] of Object.entries(toTailwindFonts())) {
    lines.push(`  --font-${name}: ${stack.join(', ')};`);
  }

  return `@theme inline {\n${lines.join('\n')}\n}\n`;
}

/**
 * Variables CSS `--stela-*` en claro y oscuro (ITSM #1839680, lote 6.6). Salen de este módulo y de
 * los valores que solo existen como CSS (escala de tipografía y espacios, sombras, movimiento,
 * alias heredados y colores del menú). Solo se declaran las que algo lee: 134 variables del
 * antiguo `theme-kit.tokens.css` (ya borrado) quedaron en 99 en claro y 40 en oscuro. Ojo: `--stela-gold-soft`
 * solo existe en oscuro, igual que antes; en claro varios componentes la leen sin valor de reserva.
 */
export function toStelaCssVariables(): { light: Array<[string, string]>; dark: Array<[string, string]> } {
  const { brand, neutral, status, radius, font, darkScheme } = stela2Tokens;

  const light: Array<[string, string]> = [
    ['gold', brand.gold],
    ['gold-light', brand.goldLight],
    ['gold-dark', brand.goldDark],
    ['navy', brand.navy],
    ['navy-light', brand.navyLight],
    ['navy-dark', brand.navyDark],
    ['aubergine', brand.aubergine],
    ['aubergine-light', brand.aubergineLight],
    ['aubergine-dark', brand.aubergineDark],
    ['aubergine-deep', brand.aubergineDeep],
    ['terracotta', brand.terracotta],
    ['terracotta-hover', brand.terracottaHover],
    ['mobile-bg', brand.mobileBg],
    ['mobile-surface', brand.mobileSurface],
    ['mobile-surface-2', brand.mobileSurface2],
    ['azure', brand.azure],
    ['azure-hover', brand.azureHover],
    ['white', '#FFFFFF'],
    ['bg', neutral.canvas],
    ['bg-2', neutral.canvas2],
    ['surface', neutral.surface],
    ['line', neutral.line],
    ['line-soft', neutral.lineSoft],
    ['fg', neutral.fg],
    ['fg-1', neutral.fg1],
    ['fg-2', neutral.fg2],
    ['fg-3', neutral.fg3],
    ['fg-4', neutral.fg4],
    ['success', status.success.DEFAULT],
    ['warning', status.warning.DEFAULT],
    ['danger', status.danger.DEFAULT],
    ['info', status.info.DEFAULT],
    ['success-bg', status.success.soft],
    ['warning-bg', status.warning.soft],
    ['danger-bg', status.danger.soft],
    ['info-bg', status.info.soft],
    ['font-display', font.display],
    ['font-sans', font.sans],
    ['font-mono', font.mono],
    ['fs-tiny', '0.75rem'],
    ['fw-regular', '400'],
    ['fw-medium', '500'],
    ['fw-semibold', '600'],
    ['fw-bold', '700'],
    ['lh-snug', '1.4'],
    ['space-1', '4px'],
    ['space-2', '8px'],
    ['space-3', '12px'],
    ['space-4', '16px'],
    ['radius-sm', radius.sm],
    ['radius-md', radius.md],
    ['radius-lg', radius.lg],
    ['radius-xl', radius.xl],
    ['radius-pill', radius.pill],
    ['shadow-sm', '0 1px 3px rgba(26, 39, 68, 0.08)'],
    ['shadow-md', '0 4px 12px rgba(26, 39, 68, 0.10)'],
    ['shadow-lg', '0 8px 30px rgba(26, 39, 68, 0.12)'],
    ['ease', 'cubic-bezier(0.4, 0, 0.2, 1)'],
    ['dur-fast', '0.18s'],
    ['dur-base', '0.3s'],
    ['font-family', 'var(--stela-font-sans)'],
    ['font-size-base', '14px'],
    ['font-size-sm', '13px'],
    ['font-size-md', '14px'],
    ['color-primary', 'var(--stela-gold)'],
    ['color-success', 'var(--stela-success)'],
    ['color-info', 'var(--stela-info)'],
    ['color-warning', 'var(--stela-warning)'],
    ['color-danger', 'var(--stela-danger)'],
    ['bg-app', 'var(--stela-bg)'],
    ['bg-surface', 'var(--stela-surface)'],
    ['bg-soft', '#F9FAFB'],
    ['bg-muted', '#F0F2F5'],
    ['bg-elevated', '#ECEEF2'],
    ['chip-bg', '#F3F5F7'],
    ['text-primary', 'var(--stela-fg)'],
    ['text-secondary', 'var(--stela-fg-2)'],
    ['text-muted', 'var(--stela-fg-3)'],
    ['text-on-primary', 'var(--stela-aubergine-dark)'],
    ['link', 'var(--stela-gold-dark)'],
    ['link-hover', 'var(--stela-gold)'],
    ['border-color', 'var(--stela-line)'],
    ['border-color-strong', '#D8DBE0'],
    ['border-focus', 'color-mix(in srgb, var(--stela-gold) 40%, #ffffff)'],
    ['shadow-card', 'var(--stela-shadow-sm)'],
    ['shadow-elevated', 'var(--stela-shadow-md)'],
    ['layout-sidebar-width', '240px'],
    ['layout-topbar-height', '60px'],
    ['menu-bg', 'var(--stela-aubergine)'],
    ['menu-border', 'var(--stela-aubergine-deep)'],
    ['menu-separator', 'rgba(255, 255, 255, 0.10)'],
    ['menu-item', '#C9C2CB'],
    ['menu-item-hover-bg', 'var(--stela-aubergine-light)'],
    ['menu-item-hover-color', '#FFFFFF'],
    ['menu-item-active-bg', 'rgba(201, 168, 76, 0.10)'],
    ['menu-item-active-color', '#FFFFFF'],
    ['menu-text-muted', '#8E8492'],
    ['spacing-3', 'var(--stela-space-3)'],
    ['spacing-4', 'var(--stela-space-4)'],
  ];

  const dark: Array<[string, string]> = [
    ['bg', darkScheme.background],
    ['bg-2', '#221624'],
    ['surface', darkScheme.card],
    ['line', darkScheme.border],
    ['line-soft', darkScheme.muted],
    ['fg', darkScheme.foreground],
    ['fg-1', '#F8F2F4'],
    ['fg-2', '#CFC2C8'],
    ['fg-3', darkScheme.mutedForeground],
    ['fg-4', '#6B5D67'],
    ['gold-soft', 'rgba(201, 168, 76, 0.18)'],
    ['success-bg', 'rgba(45, 211, 111, 0.14)'],
    ['warning-bg', 'rgba(255, 196, 9, 0.14)'],
    ['danger-bg', 'rgba(186, 21, 27, 0.18)'],
    ['info-bg', 'rgba(59, 130, 246, 0.14)'],
    ['shadow-sm', '0 1px 2px rgba(0, 0, 0, 0.4)'],
    ['shadow-md', '0 4px 14px rgba(0, 0, 0, 0.45)'],
    ['shadow-lg', '0 12px 36px rgba(0, 0, 0, 0.55)'],
    ['bg-app', 'var(--stela-bg)'],
    ['bg-surface', 'var(--stela-surface)'],
    ['bg-soft', 'var(--stela-bg-2)'],
    ['bg-muted', 'var(--stela-bg-2)'],
    ['bg-elevated', 'var(--stela-bg-2)'],
    ['chip-bg', 'var(--stela-bg-2)'],
    ['text-primary', 'var(--stela-fg)'],
    ['text-secondary', 'var(--stela-fg-2)'],
    ['text-muted', 'var(--stela-fg-3)'],
    ['text-on-primary', 'var(--stela-aubergine-dark)'],
    ['link', 'var(--stela-gold)'],
    ['link-hover', 'var(--stela-gold-light)'],
    ['border-color', 'var(--stela-line)'],
    ['border-color-strong', 'var(--stela-fg-4)'],
    ['border-focus', 'color-mix(in srgb, var(--stela-gold) 60%, #1A1019)'],
    ['shadow-card', 'var(--stela-shadow-sm)'],
    ['shadow-elevated', 'var(--stela-shadow-md)'],
    ['menu-bg', 'var(--stela-aubergine)'],
    ['menu-border', 'var(--stela-aubergine-dark)'],
    ['menu-separator', 'rgba(255, 255, 255, 0.08)'],
    ['menu-item-hover-bg', 'var(--stela-aubergine-light)'],
    ['menu-item-active-bg', 'rgba(201, 168, 76, 0.10)'],
  ];

  return { light, dark };
}

/**
 * Hoja con las variables `--stela-*`. El oscuro va en `html[data-color-scheme="dark"]`, el mismo
 * atributo que usan Optimus (`darkModeSelector`) y la variante `dark` de Tailwind; lo pone
 * `ThemeStateService`. `npm run tokens:css` la escribe en `stela2.tokens.css`.
 */
export function toStelaCssVariablesCss(): string {
  const { light, dark } = toStelaCssVariables();
  const block = (rows: Array<[string, string]>) => rows.map(([name, value]) => `  --stela-${name}: ${value};`).join('\n');
  return `:root {\n${block(light)}\n}\n\nhtml[data-color-scheme="dark"] {\n${block(dark)}\n}\n`;
}
